/**
 * SEO helpers for SPA: meta tags + canonical + JSON-LD (no SSR).
 * Absolute URLs use VITE_SITE_URL in production; fallback to window.location.origin.
 */

import { ORGANIZATION_SAME_AS } from "@/config/appStoreLinks.js";
import { robotsForProduct, robotsForRouteName, robotsForCategory } from "./seo-indexable.js";

/** الاسم العربي الأساسي للعلامة — يُعرَّف في docs/SEO_BRAND_CONTEXT.md */
export const SITE_NAME = "طازج للذبائح واللحوم";

/** اسم شائع تاريخياً في الواجهة؛ يُستخدم في alternateName وschema.org للاتساق */
export const SITE_NAME_ALTERNATE = "طازج الغذائية";

/** اسم سابق في العناوين والإعلانات — يُبقى في alternateName لاستمرار الربط في البحث */
export const SITE_NAME_LEGACY = "تطبيق الطازج للحوم";

/** رقم التواصل العام المعروض في الواجهة (wa.me/966114501893 — TabBar، إلخ). E.164 لـ schema.org telephone */
export const PUBLIC_CONTACT_TELEPHONE_E164 = "+966114501893";

/**
 * عنوان الصفحة الرئيسية للمحركات — عربي أولًا، ضمن حد القصّ في نتائج البحث (~60 حرف).
 * يُستخدم في SSR و`applyRouteSeo` — يُنصح بمزامنة `index.html` يدويًا.
 */
export const HOME_SEO_TITLE = "طازج للذبائح واللحوم | نعيمي وأضاحي وتوصيل الرياض";

/** أبعاد `public/og-image.png` — يُفضَّل تحديثها إذا استُبدلت الصورة */
export const OG_IMAGE_WIDTH = 1376;
export const OG_IMAGE_HEIGHT = 768;
export const OG_IMAGE_ALT_DEFAULT =
    "طازج للذبائح واللحوم — ذبائح ولحوم طازجة وأضاحي مع توصيل للمناطق المدعومة";

/** وصف احتياطي لمسارات بلا meta مخصص — يُستخدم أيضاً في SSR (`seo-ssr.js`) */
export const DEFAULT_DESCRIPTION =
    "طازج للذبائح واللحوم — لحوم طازجة وذبائح وأضاحي مع توصيل في الرياض والمناطق المدعومة في السعودية، ودفع آمن ومتابعة للطلب من الموقع والتطبيق.";

/** Bing يُبلّغ عن وصف أقصر من ~70 حرفاً؛ الهدف العملي لـ SERP هو 140–160. */
export const META_DESCRIPTION_MIN = 140;
export const META_DESCRIPTION_MAX = 160;

/** Per-route descriptions (Arabic, 140–160 chars for indexable pages) keyed by route.name */
export const ROUTE_DESCRIPTIONS = {
    home: "اطلب لحومًا طازجة وذبائح ونعيمي وأضاحيًا حسب التوفر من طازج للذبائح واللحوم، مع توصيل منظم للرياض والمناطق المدعومة في السعودية.",
    "fast-delivery-home":
        "التوصيل السريع من طازج للذبائح واللحوم: اطلب لحومًا ومنتجات طازجة واستلمها ضمن أوقات الخدمة المتاحة في الرياض والمناطق المدعومة.",
    "fast-delivery-tracking-order": "متابعة حالة طلب التوصيل السريع من طازج في خطوات بسيطة.",
    cart: "سلة مشترياتك في طازج — راجع منتجاتك والكميات قبل إتمام الطلب.",
    "user-profile-page": "إدارة بيانات حسابك وعناوين التوصيل في طازج للذبائح واللحوم.",
    "gest-profile-page": "الدخول كزائر أو إنشاء حساب للاستفادة من عروض وخدمات طازج.",
    "my-orders": "عرض ومتابعة طلباتك السابقة والحالية في طازج للذبائح واللحوم.",
    "order-message": "الرد على رسائل الطلب أو متابعة حالة الدفع والتوصيل.",
    "tamara-callback": "إتمام عملية الدفع أو متابعة نتيجة الطلب.",
    "tamara-status-callback": "متابعة حالة الدفع عبر تمارا لطلبك من طازج.",
    login: "تسجيل الدخول إلى حسابك في طازج للذبائح واللحوم باستخدام رقم الجوال.",
    registerPage: "إنشاء حساب جديد في طازج للطلب والتوصيل والعروض.",
    verifyPage: "التحقق من رقم الجوال لإتمام تسجيل الدخول في طازج.",
    addresses: "إدارة عناوين التوصيل المحفوظة في حسابك.",
    "fast-delivery-address": "اختيار عنوان التوصيل لخدمة التوصيل السريع من طازج.",
    map: "تحديد الموقع على الخريطة لعنوان التوصيل.",
    "cart-confirmation": "تأكيد طلبك واختيار موعد التوصيل وطريقة الدفع في طازج.",
    "fast-cart-confirmation": "تأكيد طلب التوصيل السريع من طازج قبل الدفع.",
    about: "تعرّف على طازج للذبائح واللحوم: متجر إلكتروني لنعيمي وأضاحي ولحوم طازجة، مع توصيل منظم للرياض والمناطق المدعومة في السعودية.",
    "privacy-policy":
        "سياسة الخصوصية والإستخدام لطازج: الخصوصية، المحفظة، الاسترداد، نقاط الولاء، وشروط الحساب عبر الموقع والتطبيق.",
    "return-refund-policy":
        "سياسة الإرجاع والاسترداد لطلبات طازج للذبائح واللحوم: شروط الإلغاء للمنتجات الطازجة، الاسترداد، وما يمكن إرجاعه من الموقع والتطبيق.",
    "online-payment": "إتمام الدفع الإلكتروني لطلبك بأمان عبر بوابات الدفع المعتمدة.",
    "product-detail":
        "صفحة منتج في طازج للذبائح واللحوم — الوزن والتقطيع والسعر، مع طلب ذبائح ولحوم طازجة وتوصيل للرياض والمناطق المدعومة في السعودية.",
    "category-detail":
        "تصفّح منتجات هذا القسم في طازج للذبائح واللحوم — اطلب لحومًا وذبائحًا طازجة مع توصيل للرياض والمناطق المدعومة في السعودية.",
    "blog-list":
        "مدونة طازج للذبائح واللحوم: مقالات عن طلب اللحوم والذبائح والأضاحي أونلاين، والتوصيل في الرياض والسعودية، ونصائح اختيار القطعة الطازجة.",
    "blog-article":
        "مقال من مدونة طازج للذبائح واللحوم عن اللحوم والذبائح والأضاحي والتوصيل في الرياض والسعودية، مع نصائح عملية للطلب الأونلاين.",
    "recipes-list":
        "وصفات طازج باللحم الطازج — مقادير وطريقة تحضير لأطباق يومية، مع منتجات متاحة للطلب والتوصيل في الرياض والمناطق المدعومة في السعودية.",
    "recipe-detail":
        "وصفة طبخ من طازج للذبائح واللحوم بالمقادير والطريقة، باللحم الطازج المناسب لسفرتك مع إمكانية الطلب والتوصيل في الرياض.",
};

const HTML_ENTITY_MAP = {
    nbsp: " ",
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
};

function decodeHtmlEntities(value) {
    let cur = String(value || "");
    for (let i = 0; i < 3; i += 1) {
        const next = cur.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (_, ent) => {
            const e = String(ent).toLowerCase();
            if (HTML_ENTITY_MAP[e]) return HTML_ENTITY_MAP[e];
            if (e.startsWith("#x")) {
                const n = parseInt(e.slice(2), 16);
                return Number.isFinite(n) ? String.fromCharCode(n) : " ";
            }
            if (e.startsWith("#")) {
                const n = parseInt(e.slice(1), 10);
                return Number.isFinite(n) ? String.fromCharCode(n) : " ";
            }
            return " ";
        });
        if (next === cur) break;
        cur = next;
    }
    return cur;
}

/** قصّ الوصف عند حد SERP مع محاولة القطع عند مسافة. */
export function clipMetaDescription(text, max = META_DESCRIPTION_MAX) {
    const t = String(text || "").replace(/\s+/g, " ").trim();
    if (t.length <= max) return t;
    const sliced = t.slice(0, Math.max(0, max - 1));
    const lastSpace = sliced.lastIndexOf(" ");
    const base = lastSpace >= Math.floor(max * 0.55) ? sliced.slice(0, lastSpace) : sliced;
    return `${base.trimEnd()}…`;
}

/**
 * يطيل الوصف القصير إلى 140–160 حرفاً (عتبة Bing «too short» ~70؛ الهدف 150–160).
 * @param {string} text
 * @param {string[]} [extras]
 */
export function ensureMetaDescriptionLength(text, extras = []) {
    let d = stripHtmlToText(text || "");
    for (const extra of extras) {
        if (d.length >= META_DESCRIPTION_MIN) break;
        const piece = stripHtmlToText(extra || "");
        if (!piece) continue;
        if (d && d.includes(piece)) continue;
        if (piece.includes(d) && piece.length > d.length && d.length > 0) {
            d = piece;
            continue;
        }
        d = d ? `${d} ${piece}` : piece;
    }
    const fillers = [
        "اطلب من طازج للذبائح واللحوم.",
        "توصيل ضمن المناطق المدعومة في السعودية.",
        DEFAULT_DESCRIPTION,
    ];
    for (const extra of fillers) {
        if (d.length >= META_DESCRIPTION_MIN) break;
        const piece = stripHtmlToText(extra);
        if (!piece || (d && d.includes(piece))) continue;
        d = d ? `${d} ${piece}` : piece;
    }
    if (!d) {
        d = DEFAULT_DESCRIPTION;
    }
    return clipMetaDescription(d);
}

/**
 * وصف الصفحة الرئيسية لـ SERP (عربي، 140–160 حرفًا).
 */
export const HOME_META_DESCRIPTION = ensureMetaDescriptionLength(ROUTE_DESCRIPTIONS.home, [
    "ذبائح ولحوم طازجة مع توصيل للمنزل في الرياض والمناطق المدعومة.",
]);

/** @deprecated استخدم HOME_META_DESCRIPTION */
export const HOME_META_DESCRIPTION_BILINGUAL = HOME_META_DESCRIPTION;

/** وصف منتج للفهرسة: ينظّف HTML/CSS من أودو ويُكمل الطول إن نقص. */
export function productMetaDescription(product) {
    const name = (product?.name && String(product.name).trim()) || "منتج";
    const fromHtml = stripHtmlToText(product?.description || "");
    return ensureMetaDescriptionLength(fromHtml, [
        `اطلب ${name} من ${SITE_NAME}.`,
        "ذبائح ولحوم طازجة مع توصيل للرياض والمناطق المدعومة في السعودية.",
    ]);
}

/** وصف تصنيف رئيسي للفهرسة ومعاينة المشاركة. */
export function categoryMetaDescription(category) {
    const name = (category?.name && String(category.name).trim()) || "تصنيف";
    return ensureMetaDescriptionLength("", [
        `تسوق ${name} من ${SITE_NAME}.`,
        "ذبائح ولحوم طازجة مع توصيل للرياض والمناطق المدعومة في السعودية.",
    ]);
}

export function getCategoryShareImageUrl(category, base) {
    const siteBase = base || getSiteBaseUrl();
    if (category?.id != null && category?.cover_image) {
        return `${siteBase}/category-image/${category.id}.jpg`;
    }
    return `${siteBase}/og-image.png`;
}

export function categoryPagePath(categoryId) {
    return `/category/${categoryId}`;
}

/** وصف مسار مفهرس؛ الصفحة الرئيسية عربي ضمن نافذة SERP. */
export function resolveRouteMetaDescription(routeName, metaDesc) {
    if (routeName === "home") {
        return HOME_META_DESCRIPTION;
    }
    const raw =
        (typeof metaDesc === "string" && metaDesc.length > 0
            ? metaDesc
            : ROUTE_DESCRIPTIONS[routeName]) || DEFAULT_DESCRIPTION;
    if (robotsForRouteName(routeName).startsWith("noindex")) {
        return stripHtmlToText(raw) || DEFAULT_DESCRIPTION;
    }
    return ensureMetaDescriptionLength(raw, [DEFAULT_DESCRIPTION]);
}

function getSiteBaseUrl() {
    const fromEnv = import.meta.env.VITE_SITE_URL;
    if (fromEnv && typeof fromEnv === "string") {
        return fromEnv.replace(/\/$/, "");
    }
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "https://www.tazejfood.com";
}

function ensureMeta(attrName, attrValue, content) {
    let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function ensureProperty(property, content) {
    ensureMeta("property", property, content);
}

function removeOgImageDimensionMetas() {
    if (typeof document === "undefined") {
        return;
    }
    document
        .querySelectorAll('meta[property="og:image:width"], meta[property="og:image:height"]')
        .forEach((el) => el.remove());
}

/**
 * رابط صورة المشاركة للمنتج (PNG عبر السيرفر) أو الصورة الافتراضية.
 */
export function getProductShareImageUrl(product, base) {
    const b = String(base).replace(/\/$/, "");
    if (product?.id != null && product?.image && String(product.image).trim().length > 0) {
        return `${b}/product-image/${product.id}.jpg`;
    }
    return `${b}/og-image.png`;
}

/**
 * Organization.sameAs — متاجر وتطبيقات وحسابات رسمية.
 * @returns {string[]}
 */
export function organizationSameAsUrls() {
    return [...ORGANIZATION_SAME_AS];
}

/**
 * عقد Organization الأساسي لـ @graph.
 * @param {string} base
 */
export function buildOrganizationJsonLd(base) {
    const b = String(base).replace(/\/$/, "");
    return {
        "@type": "Organization",
        "@id": `${b}/#organization`,
        name: SITE_NAME,
        alternateName: [SITE_NAME_ALTERNATE, SITE_NAME_LEGACY, "Tazej Food"],
        url: b,
        logo: {
            "@type": "ImageObject",
            url: `${b}/og-image.png`,
            width: OG_IMAGE_WIDTH,
            height: OG_IMAGE_HEIGHT,
        },
        sameAs: organizationSameAsUrls(),
    };
}

/**
 * عقد LocalBusiness + OnlineStore لـ @graph (SSR و`injectJsonLdOrganization`).
 * السوق والمدينة من docs/SEO_BRAND_CONTEXT.md؛ لا عنوان شارع منشور (خدمة توصيل إلكترونية).
 * @param {string} base أصل الموقع بدون شرطة مائلة نهائية
 */
export function buildLocalBusinessJsonLd(base) {
    const b = String(base).replace(/\/$/, "");
    return {
        "@type": ["OnlineStore", "LocalBusiness"],
        "@id": `${b}/#localbusiness`,
        name: SITE_NAME,
        alternateName: [SITE_NAME_ALTERNATE, SITE_NAME_LEGACY, "Tazej Food"],
        description: ROUTE_DESCRIPTIONS.home,
        url: b,
        telephone: PUBLIC_CONTACT_TELEPHONE_E164,
        image: `${b}/og-image.png`,
        priceRange: "$$",
        address: {
            "@type": "PostalAddress",
            addressCountry: "SA",
            addressRegion: "Riyadh Province",
            addressLocality: "Riyadh",
        },
        areaServed: [
            {
                "@type": "City",
                name: "Riyadh",
                alternateName: "الرياض",
            },
            {
                "@type": "Country",
                name: "Saudi Arabia",
                alternateName: "المملكة العربية السعودية",
            },
        ],
        parentOrganization: { "@id": `${b}/#organization` },
    };
}

/**
 * عقد Product + BreadcrumbList لصفحة المنتج (مشاركة بين العميل و SSR).
 * @param {object} product
 * @param {string} base
 * @param {{ categoryName?: string, isFastDelivery?: boolean }} [ctx]
 */
export function buildProductJsonLdGraph(product, base, ctx = {}) {
    const b = String(base).replace(/\/$/, "");
    const url = `${b}/product/${product.id}`;
    const desc = productMetaDescription(product);
    const shareImageUrl = getProductShareImageUrl(product, b);
    const price = ((product.variants?.[0]?.list_price ?? 0) * 1.15).toFixed(2);
    const sku =
        product.default_code != null && String(product.default_code).trim() !== ""
            ? String(product.default_code).trim()
            : `TAZEJ-${product.id}`;

    const productNode = {
        "@type": "Product",
        "@id": `${url}#product`,
        name: product.name,
        description: desc,
        image: [shareImageUrl],
        url,
        sku,
        brand: {
            "@type": "Brand",
            name: SITE_NAME,
        },
        offers: {
            "@type": "Offer",
            priceCurrency: "SAR",
            price,
            availability: "https://schema.org/InStock",
            url,
        },
    };

    const cat = ctx.categoryName != null ? String(ctx.categoryName).trim() : "";
    if (cat) {
        productNode.category = cat;
    }

    const crumbItems = [];
    let pos = 1;
    crumbItems.push({
        "@type": "ListItem",
        position: pos++,
        name: "الرئيسية",
        item: `${b}/`,
    });
    if (ctx.isFastDelivery) {
        crumbItems.push({
            "@type": "ListItem",
            position: pos++,
            name: "التوصيل السريع",
            item: `${b}/fast-delivery`,
        });
    }
    crumbItems.push({
        "@type": "ListItem",
        position: pos,
        name: product.name,
        item: url,
    });

    const breadcrumbNode = {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: crumbItems,
    };

    return [productNode, breadcrumbNode];
}

/**
 * Canonical / share URL for a blog article on the storefront (`/blog/:slug`).
 * @param {object} article
 * @param {string} base
 */
export function blogArticlePageUrl(article, base) {
    const b = String(base).replace(/\/$/, "");
    let baseHost = "";
    try {
        baseHost = new URL(b).hostname.replace(/^www\./i, "");
    } catch {
        baseHost = "";
    }
    /** `canonical_url` نسبي أو على نفس الم hostname فقط — لا نعتمد روابط خارجية قديمة. */
    const c = article?.canonical_url;
    if (c && typeof c === "string") {
        const t = c.trim();
        if (/^https?:\/\//i.test(t)) {
            try {
                const u = new URL(t);
                const h = u.hostname.replace(/^www\./i, "");
                if (!baseHost || h === baseHost) {
                    return t.replace(/\/$/, "");
                }
            } catch {
                /* ignore */
            }
        } else if (t.startsWith("/")) {
            return `${b}${t}`.replace(/\/$/, "");
        }
    }
    const slug = encodeURIComponent(String(article?.slug || ""));
    return `${b}/blog/${slug}`;
}

/**
 * صورة OG للمدونة: مسار مطلق على النطاق أو URL خارجي؛ يُحوَّل إلى مطلق للميتا وschema.org.
 * @param {unknown} raw — من بيانات المقال (`og_image_url`)
 * @param {string} base — أصل الموقع (`VITE_SITE_URL`)
 */
export function resolveBlogOgImageUrl(raw, base) {
    const b = String(base).replace(/\/$/, "");
    const fallback = `${b}/og-image.png`;
    if (raw === null || raw === undefined) {
        return fallback;
    }
    const t = String(raw).trim();
    if (!t) {
        return fallback;
    }
    if (/^https?:\/\//i.test(t)) {
        return t.replace(/\/$/, "");
    }
    const path = t.startsWith("/") ? t : `/${t}`;
    return `${b}${path}`;
}

export function blogArticleDescription(article) {
    const title = (article?.title && String(article.title).trim()) || "مقال";
    const raw =
        (typeof article.meta_description === "string" && article.meta_description.trim()) ||
        (typeof article.excerpt === "string" && article.excerpt.trim()) ||
        stripHtmlToText(article.text_plain || article.content_html || "");
    return ensureMetaDescriptionLength(raw, [
        `${title} — ${SITE_NAME}.`,
        "ذبائح ولحوم طازجة مع توصيل للرياض والمناطق المدعومة في السعودية.",
    ]);
}

function blogOgLocale(lang) {
    if (!lang || typeof lang !== "string") return "ar_SA";
    const x = lang.replace(/-/g, "_");
    if (x.includes("_")) return x;
    return `${x}_SA`;
}

/**
 * @param {object} article
 * @param {string} base
 * @returns {object[]}
 */
export function buildBlogArticleJsonLdGraph(article, base) {
    const b = String(base).replace(/\/$/, "");
    const url = blogArticlePageUrl(article, b);
    const img = resolveBlogOgImageUrl(article?.og_image_url, b);
    const desc = blogArticleDescription(article);
    const posting = {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: article.title || "",
        image: [img],
        url,
        author: {
            "@type": "Person",
            name: (article.author_name && String(article.author_name).trim()) || SITE_NAME,
        },
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: {
                "@type": "ImageObject",
                url: `${b}/og-image.png`,
                width: OG_IMAGE_WIDTH,
                height: OG_IMAGE_HEIGHT,
            },
        },
        description: desc,
        inLanguage: article.lang || "ar-SA",
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
    };
    if (article.publish_date) {
        posting.datePublished = article.publish_date;
    }
    if (article.updated_date || article.publish_date) {
        posting.dateModified = article.updated_date || article.publish_date;
    }
    const crumbs = [
        { "@type": "ListItem", position: 1, name: "الرئيسية", item: `${b}/` },
        { "@type": "ListItem", position: 2, name: "المدونة", item: `${b}/blog` },
        { "@type": "ListItem", position: 3, name: article.title || "مقال", item: url },
    ];
    const breadcrumb = {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: crumbs,
    };
    return [posting, breadcrumb];
}

/**
 * @param {string} base
 * @param {string} [imageAlt]
 * @param {string | null} [explicitImageUrl] إن وُجد وليس og-image الافتراضية، لا نضيف أبعاد og:image
 */
function applyOgImageBundle(base, imageAlt = OG_IMAGE_ALT_DEFAULT, explicitImageUrl = null) {
    const defaultUrl = `${base}/og-image.png`;
    const imageUrl = explicitImageUrl || defaultUrl;
    ensureProperty("og:image", imageUrl);
    if (imageUrl === defaultUrl) {
        ensureProperty("og:image:width", String(OG_IMAGE_WIDTH));
        ensureProperty("og:image:height", String(OG_IMAGE_HEIGHT));
    } else {
        removeOgImageDimensionMetas();
    }
    ensureProperty("og:image:alt", imageAlt);
}

function ensureLinkRel(rel, href) {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

/** وصف نصي من HTML (يُستخدم في SSR وصفحة المنتج). */
export function stripHtmlToText(html) {
    if (!html || typeof html !== "string") return "";
    let raw;
    if (typeof document !== "undefined") {
        const d = document.createElement("div");
        d.innerHTML = html;
        raw = d.textContent || "";
    } else {
        raw = html
            .replace(/<style[\s\S]*?<\/style>/gi, " ")
            .replace(/<script[\s\S]*?<\/script>/gi, " ")
            .replace(/<[^>]+>/g, " ");
    }
    raw = decodeHtmlEntities(raw);
    raw = raw.replace(/[a-z.#][\w-]*\s*\{[^}]*\}/gi, " ");
    return raw.replace(/\s+/g, " ").trim();
}

/**
 * Apply title, meta description, Open Graph, Twitter, canonical from Vue Router route.
 */
export function applyRouteSeo(route) {
    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-blog"]').forEach((el) => el.remove());

    const routeName = route.name;
    if (routeName === "product-detail") {
        document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());
        return;
    }
    if (routeName === "category-detail") {
        document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());
        return;
    }
    if (routeName === "blog-article") {
        document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());
        return;
    }

    const base = getSiteBaseUrl();
    const path = route.path || "/";
    const canonicalPath = path.split("?")[0].split("#")[0] || "/";
    const url = `${base}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;

    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const pageTitle = route.meta?.title;
    const description = resolveRouteMetaDescription(routeName, route.meta?.description);

    const fullTitle =
        routeName === "home"
            ? HOME_SEO_TITLE
            : pageTitle
              ? `${SITE_NAME} | ${pageTitle}`
              : SITE_NAME;

    document.title = fullTitle;

    ensureMeta("name", "description", description);
    ensureMeta("name", "robots", robotsForRouteName(routeName));
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", fullTitle);
    ensureProperty("og:description", description);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    applyOgImageBundle(base);

    const ogImageContent =
        document.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
        `${base}/og-image.png`;
    const ogImageAltContent =
        document.querySelector('meta[property="og:image:alt"]')?.getAttribute("content") ||
        OG_IMAGE_ALT_DEFAULT;

    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", fullTitle);
    ensureMeta("name", "twitter:description", description);
    ensureMeta("name", "twitter:image", ogImageContent);
    ensureMeta("name", "twitter:image:alt", ogImageAltContent);

    ensureLinkRel("canonical", url);
}

let jsonLdInjected = false;

/**
 * Organization + WebSite JSON-LD (once).
 */
export function injectJsonLdOrganization() {
    if (jsonLdInjected || typeof document === "undefined") return;
    if (document.querySelector('script[data-seo="jsonld-organization"]')) {
        jsonLdInjected = true;
        return;
    }
    jsonLdInjected = true;

    const base = getSiteBaseUrl();
    const data = {
        "@context": "https://schema.org",
        "@graph": [
            buildOrganizationJsonLd(base),
            buildLocalBusinessJsonLd(base),
            {
                "@type": "WebSite",
                "@id": `${base}/#website`,
                url: base,
                name: SITE_NAME,
                description: HOME_META_DESCRIPTION,
                inLanguage: "ar-SA",
                publisher: { "@id": `${base}/#organization` },
            },
        ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld-organization");
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
}

/**
 * SEO بعد تحميل مقال المدونة (عميل فقط؛ SSR يستخدم `buildBlogArticleSsrHeadBlock`).
 * @param {object} article
 */
export function applyBlogArticlePageSeo(article) {
    if (!article?.slug || typeof document === "undefined") {
        return;
    }

    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-blog"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const url = blogArticlePageUrl(article, base);
    const titleRaw =
        (typeof article.meta_title === "string" && article.meta_title.trim()) || article.title || "مقال";
    const title = `${SITE_NAME} | ${titleRaw}`;
    const desc = blogArticleDescription(article);
    const indexable = article.is_indexable !== false;
    const robots = indexable ? "index, follow" : "noindex, follow";
    const ogImage = resolveBlogOgImageUrl(article?.og_image_url, base);
    const defaultOg = `${String(base).replace(/\/$/, "")}/og-image.png`;
    const ogAlt = `${titleRaw} — ${SITE_NAME}`;
    const ogLoc = blogOgLocale(article.lang);

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", robots);
    ensureProperty("og:type", "article");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", ogLoc);
    if (article.publish_date) {
        ensureProperty("article:published_time", String(article.publish_date));
    }
    if (article.updated_date || article.publish_date) {
        ensureProperty("article:modified_time", String(article.updated_date || article.publish_date));
    }
    ensureProperty("og:image", ogImage);
    if (ogImage === defaultOg) {
        ensureProperty("og:image:width", String(OG_IMAGE_WIDTH));
        ensureProperty("og:image:height", String(OG_IMAGE_HEIGHT));
    } else {
        removeOgImageDimensionMetas();
    }
    ensureProperty("og:image:alt", ogAlt);

    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImage);
    ensureMeta("name", "twitter:image:alt", ogAlt);

    ensureLinkRel("canonical", url);

    const ld = {
        "@context": "https://schema.org",
        "@graph": buildBlogArticleJsonLdGraph(article, base),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld-blog");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
}

/** عميل: صفحة مقال غير موجود أو خطأ تحميل */
export function applyBlogArticleFallbackSeo() {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelectorAll('script[data-seo="jsonld-blog"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const path = window.location.pathname.split("?")[0] || "/";
    const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const title = `${SITE_NAME} | المقال غير متوفر`;
    const desc = "المقال غير متوفر أو لم يعد منشورًا.";

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", "noindex, follow");
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    applyOgImageBundle(base);
    const ogImageContent =
        document.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
        `${base}/og-image.png`;
    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImageContent);
    ensureLinkRel("canonical", url);
}

/**
 * Canonical URL for a recipe (`/recipes/:slug`).
 * @param {object} recipe
 * @param {string} base
 */
export function recipePageUrl(recipe, base) {
    const b = String(base).replace(/\/$/, "");
    const slug = encodeURIComponent(String(recipe?.slug || ""));
    return `${b}/recipes/${slug}`;
}

export function recipeDescription(recipe) {
    const title = (recipe?.title && String(recipe.title).trim()) || "وصفة";
    const raw =
        (typeof recipe.meta_description === "string" && recipe.meta_description.trim()) ||
        (typeof recipe.excerpt === "string" && recipe.excerpt.trim()) ||
        stripHtmlToText(recipe.text_plain || "");
    return ensureMetaDescriptionLength(raw, [
        `وصفة ${title} من ${SITE_NAME} — مقادير وطريقة تحضير باللحم الطازج.`,
        "اطلب المكونات مع توصيل للرياض والمناطق المدعومة في السعودية.",
    ]);
}

/**
 * يستخرج نصوص عناصر القائمة من HTML (مقادير الوصفة).
 * @param {string} html
 * @returns {string[]}
 */
export function extractRecipeIngredientsFromHtml(html) {
    if (!html || typeof html !== "string") return [];
    const items = [];
    const liRe = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
    let m;
    while ((m = liRe.exec(html)) !== null) {
        const text = stripHtmlToText(m[1]);
        if (text) items.push(text);
    }
    if (items.length) return items;
    const plain = stripHtmlToText(html);
    if (!plain) return [];
    return plain
        .split(/\n+|•|·|(?=\d+[\).\-\u060c،]\s)/)
        .map((s) => s.replace(/^[-–•\d\.\)\s]+/, "").trim())
        .filter((s) => s.length > 1);
}

/**
 * يستخرج خطوات HowToStep من HTML الطريقة.
 * @param {string} html
 * @returns {object[]}
 */
export function extractRecipeInstructionsFromHtml(html) {
    if (!html || typeof html !== "string") return [];
    const steps = [];
    const liRe = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
    let m;
    let pos = 1;
    while ((m = liRe.exec(html)) !== null) {
        const text = stripHtmlToText(m[1]);
        if (text) {
            steps.push({
                "@type": "HowToStep",
                position: pos,
                text,
            });
            pos += 1;
        }
    }
    if (steps.length) return steps;
    const pRe = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
    while ((m = pRe.exec(html)) !== null) {
        const text = stripHtmlToText(m[1]);
        if (text && text.length > 8) {
            steps.push({
                "@type": "HowToStep",
                position: pos,
                text,
            });
            pos += 1;
        }
    }
    return steps;
}

/**
 * @param {object} recipe
 * @param {string} base
 * @returns {object[]}
 */
export function buildRecipeJsonLdGraph(recipe, base) {
    const b = String(base).replace(/\/$/, "");
    const url = recipePageUrl(recipe, b);
    const img = resolveBlogOgImageUrl(recipe?.image_url, b);
    const desc = recipeDescription(recipe);
    const ingredients = extractRecipeIngredientsFromHtml(recipe?.ingredients_html || "");
    const instructions = extractRecipeInstructionsFromHtml(recipe?.method_html || "");
    const recipeNode = {
        "@type": "Recipe",
        "@id": `${url}#recipe`,
        name: recipe.title || "",
        description: desc,
        image: [img],
        url,
        inLanguage: recipe.lang || "ar-SA",
        recipeCuisine: "Saudi",
        recipeCategory: "طبق رئيسي",
        keywords: ["لحم", "طبخ", "وصفة", SITE_NAME].join(", "),
        author: {
            "@type": "Organization",
            name: SITE_NAME,
            url: b,
        },
    };
    if (ingredients.length) {
        recipeNode.recipeIngredient = ingredients;
    }
    if (instructions.length) {
        recipeNode.recipeInstructions = instructions;
    }
    if (recipe.prep_time) {
        recipeNode.prepTime = String(recipe.prep_time);
    }
    if (recipe.cook_time) {
        recipeNode.cookTime = String(recipe.cook_time);
    }
    if (recipe.total_time) {
        recipeNode.totalTime = String(recipe.total_time);
    }
    if (recipe.publish_date) {
        recipeNode.datePublished = String(recipe.publish_date);
    }
    if (recipe.updated_date) {
        recipeNode.dateModified = String(recipe.updated_date);
    }
    const breadcrumbNode = {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "الرئيسية", item: `${b}/` },
            { "@type": "ListItem", position: 2, name: "الوصفات", item: `${b}/recipes` },
            { "@type": "ListItem", position: 3, name: recipe.title || "وصفة", item: url },
        ],
    };
    return [recipeNode, breadcrumbNode];
}

/**
 * SEO بعد تحميل وصفة (عميل؛ SSR يستخدم `buildRecipeSsrHeadBlock`).
 * @param {object} recipe
 */
export function applyRecipePageSeo(recipe) {
    if (!recipe?.slug || typeof document === "undefined") {
        return;
    }

    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-blog"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-recipe"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const url = recipePageUrl(recipe, base);
    const titleRaw =
        (typeof recipe.meta_title === "string" && recipe.meta_title.trim()) || recipe.title || "وصفة";
    const title = `${SITE_NAME} | ${titleRaw}`;
    const desc = recipeDescription(recipe);
    const indexable = recipe.is_indexable !== false;
    const robots = indexable ? "index, follow" : "noindex, follow";
    const ogImage = resolveBlogOgImageUrl(recipe?.image_url, base);
    const defaultOg = `${String(base).replace(/\/$/, "")}/og-image.png`;
    const ogAlt = `${titleRaw} — ${SITE_NAME}`;
    const ogLoc = blogOgLocale(recipe.lang);

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", robots);
    ensureProperty("og:type", "article");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", ogLoc);
    if (recipe.publish_date) {
        ensureProperty("article:published_time", String(recipe.publish_date));
    }
    if (recipe.updated_date || recipe.publish_date) {
        ensureProperty("article:modified_time", String(recipe.updated_date || recipe.publish_date));
    }
    ensureProperty("og:image", ogImage);
    if (ogImage === defaultOg) {
        ensureProperty("og:image:width", String(OG_IMAGE_WIDTH));
        ensureProperty("og:image:height", String(OG_IMAGE_HEIGHT));
    } else {
        removeOgImageDimensionMetas();
    }
    ensureProperty("og:image:alt", ogAlt);

    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImage);
    ensureMeta("name", "twitter:image:alt", ogAlt);

    ensureLinkRel("canonical", url);

    const ld = {
        "@context": "https://schema.org",
        "@graph": buildRecipeJsonLdGraph(recipe, base),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld-recipe");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
}

/** عميل: وصفة غير موجودة أو خطأ تحميل */
export function applyRecipeFallbackSeo() {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelectorAll('script[data-seo="jsonld-recipe"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const path = window.location.pathname.split("?")[0] || "/";
    const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const title = `${SITE_NAME} | الوصفة غير متوفرة`;
    const desc = "الوصفة غير متوفرة أو لم تعد منشورة.";

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", "noindex, follow");
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    applyOgImageBundle(base);
    const ogImageContent =
        document.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
        `${base}/og-image.png`;
    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImageContent);
    ensureLinkRel("canonical", url);
}

/**
 * SEO عند فتح صفحة منتج (/product/:id) بعد تحميل بيانات المنتج.
 * @param {object} product
 * @param {{ categoryName?: string, isFastDelivery?: boolean }} [seoCtx]
 */
export function applyProductPageSeo(product, seoCtx = {}) {
    if (!product?.name) return;
    if (typeof document === "undefined") return;

    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const url = `${base}/product/${product.id}`;
    const title = `${SITE_NAME} | ${product.name}`;
    const desc = productMetaDescription(product);

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", robotsForProduct(product, seoCtx));
    ensureProperty("og:type", "product");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    const productOgAlt = `${product.name} — ${SITE_NAME}`;
    const shareImageUrl = getProductShareImageUrl(product, base);
    applyOgImageBundle(
        base,
        productOgAlt,
        shareImageUrl === `${base}/og-image.png` ? null : shareImageUrl
    );

    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", shareImageUrl);
    ensureMeta("name", "twitter:image:alt", productOgAlt);

    ensureLinkRel("canonical", url);

    if (robotsForProduct(product, seoCtx).startsWith("noindex")) {
        document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
        return;
    }

    const ld = {
        "@context": "https://schema.org",
        "@graph": buildProductJsonLdGraph(product, base, seoCtx),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "jsonld-product");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
}

/**
 * SEO عند فتح صفحة تصنيف (/category/:id) بعد تحميل بيانات القسم.
 * @param {object} category
 */
export function applyCategoryPageSeo(category) {
    if (!category?.name || category.id == null) return;
    if (typeof document === "undefined") return;

    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const url = `${base}/category/${category.id}`;
    const title = `${SITE_NAME} | ${category.name}`;
    const desc = categoryMetaDescription(category);
    const robots = robotsForCategory(category);

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", robots);
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    const ogAlt = `${category.name} — ${SITE_NAME}`;
    const shareImageUrl = getCategoryShareImageUrl(category, base);
    applyOgImageBundle(
        base,
        ogAlt,
        shareImageUrl === `${base}/og-image.png` ? null : shareImageUrl
    );

    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", shareImageUrl);
    ensureMeta("name", "twitter:image:alt", ogAlt);

    ensureLinkRel("canonical", url);
}

/** عميل: تصنيف غير موجود — noindex */
export function applyCategoryUnavailableSeo() {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const path = window.location.pathname.split("?")[0] || "/";
    const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const title = `${SITE_NAME} | التصنيف غير متوفر`;
    const desc = "هذا القسم غير متوفر أو لم يعد ضمن العرض.";

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", "noindex, follow");
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    applyOgImageBundle(base);
    const ogImageContent =
        document.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
        `${base}/og-image.png`;
    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImageContent);
    ensureLinkRel("canonical", url);
}

/** عميل: منتج غير موجود — noindex */
export function applyProductUnavailableSeo() {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelectorAll('script[data-seo="jsonld-product"]').forEach((el) => el.remove());
    document.querySelectorAll('script[data-seo="jsonld-faq"]').forEach((el) => el.remove());

    const base = getSiteBaseUrl();
    const path = window.location.pathname.split("?")[0] || "/";
    const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
    const title = `${SITE_NAME} | المنتج غير متوفر`;
    const desc = "هذا المنتج غير متوفر أو لم يعد ضمن العرض.";

    document.title = title;
    ensureMeta("name", "description", desc);
    ensureMeta("name", "robots", "noindex, follow");
    ensureProperty("og:type", "website");
    ensureProperty("og:site_name", SITE_NAME);
    ensureProperty("og:title", title);
    ensureProperty("og:description", desc);
    ensureProperty("og:url", url);
    ensureProperty("og:locale", "ar_SA");
    applyOgImageBundle(base);
    const ogImageContent =
        document.querySelector('meta[property="og:image"]')?.getAttribute("content") ||
        `${base}/og-image.png`;
    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", desc);
    ensureMeta("name", "twitter:image", ogImageContent);
    ensureLinkRel("canonical", `${base}/`);
}
